<template>
  <BottomWrapper
    id="rule-engine-bottom-wrapper"
    v-waiting.center="isLoading = { isLoading: loading }"
    :show-back="showBack"
    :err-info="errInfo"
    :buttons="buttons"
    :cancelable-request="!showAddComment"
    @select="handleWrapperSelect"
    @retry="cleanErr"
    @back="handleBack"
    @support-request:cancel="$emit('support-request:cancel', $event)"
  >
    <div v-if="isOpeningRequest">
      <div class="form">
        <div class="sd-input">
          <textarea
            v-model="requestInfo.description"
            :placeholder="`* ${openRequestField.placeholder || openRequestField.displayName || openRequestField.name}`"
            :class="[{ warn: openRequestError.description }]"
            rows="7"
            :maxlength="MAX_DESCRIPTION_CHAR_NUM"
            @focus="openRequestError.description = false"
          />
          <p class="textarea-char" :class="{ exceed: isDescriptionExceed }">
            {{ descriptionLength }} / {{ MAX_DESCRIPTION_CHAR_NUM }}
          </p>
          <p v-if="openRequestError.description" class="warn-note-wrapper">
            <span class="star">*</span>
            <span>
              {{
                isDescriptionExceed
                  ? $t('page.guest.description.exceed', { maxChar: MAX_DESCRIPTION_CHAR_NUM })
                  : $t('page.guest.description.invalid')
              }}
            </span>
          </p>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="isSelectedLongTimeWaitingOption" class="long-time-waiting-container">
        <div class="long-time-waiting-title">
          {{ $t('page.connect.long_wait_time.title', { operation: selectedLongTimeWaitingOption }) }}
          <span class="highlight">{{
            $t('page.connect.long_wait_time.title_minutes', { limit: longTimeWaiting.MIN_WAIT_TIME })
          }}</span>
        </div>
        <div>
          <span>
            {{ $t('page.connect.long_wait_time.high_volume', { reason: highVolumeText }) }}
            <span
              v-if="hasAlternativeOption"
              v-html="$t('page.connect.long_wait_time.suggestion', { suggestion: suggestionText })"
            />
          </span>
        </div>
      </div>
      <div
        class="block-btn-container button-section__items"
        :style="isSelectedLongTimeWaitingOption && { gap: '12px' }"
      >
        <div
          v-if="showLiveChat"
          id="lo_live_chat"
          class="block-btn"
          :class="{ secondary: isLiveChatLongTimeWaiting, disabled: !hasLiveChatOnlineAgents || isVoiceChatLoading }"
          :style="{ order: !hasLiveChatOnlineAgents ? DISABLED_OPTION_WAIT_TIME : waitTimes.order.chat }"
          @click="select(1)"
        >
          <span v-if="isSelectedLongTimeWaitingOption && isLiveChatLongTimeWaiting">
            {{ $t('page.connect.long_wait_time.continue_with') }}</span
          >
          <LoadingIcon v-if="isLiveChatLoading" /> {{ $t('page.connect.live_chat') }}
        </div>
        <span
          v-if="showLiveChat && waitTimes.chat"
          :class="{ 'long-time-waiting': isLiveChatLongTimeWaiting }"
          :style="{ order: !hasLiveChatOnlineAgents ? DISABLED_OPTION_WAIT_TIME : waitTimes.order.chat }"
        >
          <span v-if="!hasLiveChatOnlineAgents">
            {{
              isInMaintenanceWindow
                ? $t('page.connect.overloaded.channel_unavailable_maintenance')
                : $t('page.connect.overloaded.channel_unavailable')
            }}
          </span>
          <span v-else class="button-description-container">
            <ExclamationIcon v-if="isLiveChatLongTimeWaiting" />
            {{ $tc('page.connect.customers_waiting', queuePosition, { customersNum: queuePosition }) }}
            {{
              $t('page.connect.wait_time', {
                mins: isLiveChatLongTimeWaiting ? `> ${longTimeWaiting.MIN_WAIT_TIME}` : waitTimes.chat,
              })
            }}
          </span>
        </span>
        <div
          v-if="showCall"
          id="lo_call"
          class="block-btn"
          :class="{
            secondary: isCallLongTimeWaiting,
            disabled: isVoiceCallDisabled,
          }"
          :style="{ order: !hasCallOnlineAgents ? DISABLED_OPTION_WAIT_TIME : waitTimes.order.call }"
          @click="select(2)"
        >
          <span v-if="isSelectedLongTimeWaitingOption && isCallLongTimeWaiting">
            {{ $t('page.connect.long_wait_time.continue_with') }}</span
          >
          <LoadingIcon v-if="isVoiceChatLoading" /> {{ $t('page.connect.call') }}
        </div>
        <span
          v-if="showCall && waitTimes.call"
          class="button-description-container"
          :class="{ 'long-time-waiting': isCallLongTimeWaiting }"
          :style="{ order: !hasCallOnlineAgents ? DISABLED_OPTION_WAIT_TIME : waitTimes.order.call }"
        >
          <span v-if="!hasCallOnlineAgents">
            {{
              isInMaintenanceWindow
                ? $t('page.connect.overloaded.channel_unavailable_maintenance')
                : $t('page.connect.overloaded.channel_unavailable')
            }}
          </span>
          <span v-else-if="voiceReadinessFailure" class="button-description-container">
            {{ voiceReadinessUnavailableMessage }}
          </span>
          <template v-else>
            <ExclamationIcon v-if="isCallLongTimeWaiting" />
            {{
              $t('page.connect.wait_time', {
                mins: isCallLongTimeWaiting ? `> ${longTimeWaiting.MIN_WAIT_TIME}` : waitTimes.call,
              })
            }}
          </template>
        </span>

        <div v-if="showOpenRequest" id="lo_request" class="block-btn" :style="{ order: 1 }" @click="select(3)">
          {{ $t('global.open_request') }}
        </div>

        <div v-if="showAddComment" id="lo_request" class="block-btn" :style="{ order: 1 }" @click="select(4)">
          {{ $t('page.connect.follow_up.text') }}
        </div>

        <span
          v-if="isSelectedLongTimeWaitingOption && hasAlternativeOption"
          class="long-time-wait-still-prefer"
          :style="{ order: longTimeWaiting.MIN_WAIT_TIME - 1 }"
        >
          {{ $t('page.connect.long_wait_time.still_prefer', { operation: stillPreferText }) }}
        </span>
      </div>
    </div>
  </BottomWrapper>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Input from '@/components/Input.vue';
import ExclamationIcon from '@/components/icons/ExclamationIcon.vue';
import { BottomWrapper } from '../BottomWrapper.vue';

import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import waiting from '@/components/loading/waiting';
import { ConnectRequestEvent } from '@/common/types';
import { reportingService } from '@/common/reporting';
import { mixins, noop } from 'vue-class-component/lib/util';
import { MAX_DESCRIPTION_CHAR_NUM } from '@/common/config';
import * as HardcodedTags from '@/common/tags';
import { OverloadedChannel, SelectedChannel, SelectedChannelType } from '@/common/bot/enum/DeflectionLog';
import { ConnectToAgent } from '@/common/bot/types';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import ThresholdLimitModal from './ThresholdLimitModal.vue';
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import { notEmpty, waitFor } from '@/common/utils';
import { Conversation } from '../active-conversations/type';
import LogPageEventMixin, { PAGE_TRACKING } from '@/mixins/logPageEvent';
import { analytics, CMP_CONTEXTS, COMPONENT_EVENTS, CRITICAL_EVENTS } from '@/common/logs';
import { BTN_EVTS } from '@/common/logs/events';
import { isRequestLimitError, parseErrorMessage } from '@/common/error';
import { generateSelectedTransactionsTags, hasSelectedTransactions } from '@/common/selectedTransactions.utils';
import {
  isVoiceReadinessEnforcementEnabled,
  isVoiceReadinessTrackingEnabled,
  runVoiceReadinessCheck,
} from '@/common/voice/voiceReadiness';
import { looselyCheckIfMaintenanceIsOngoing } from '@/common/MaintenanceLib';

/** See `src/common/voice/voiceReadiness.ts` — `VOICE_READINESS_METRICS_ENABLED` + `VOICE_READINESS_UI_ENFORCEMENT_ENABLED`. */

const userModule = namespace('user');
const channelLoadingModule = namespace('channelLoading');

@Component({
  components: {
    Input,
    BottomWrapper,
    ExclamationIcon,
    ThresholdLimitModal,
    LoadingIcon,
  },
  directives: {
    waiting,
  },
})
export default class ContactOptions extends mixins(LogPageEventMixin) {
  @Prop({ default: () => ({ subject: '', description: '', email: '' }) }) readonly requestInfo!: {
    subject: string;
    description: string;
    email: string;
  };
  @Prop({
    default: () => ({
      showCall: true,
      showLiveChat: true,
    }),
  })
  readonly showLoggedInOptions!: { showCall: boolean; showLiveChat: boolean; showOpenRequest: boolean };

  @Prop(Boolean) readonly showAddComment!: boolean;
  @Prop(Boolean) readonly showBack!: boolean;
  @Prop() readonly connectConfig!: ConnectToAgent | null;
  @Prop({ default: () => [] }) readonly ticketFields!: PleasedModel.TicketField[];
  @Prop() readonly userSelections!: PleasedModel.UserSelection[];
  @Prop() readonly showThresholdLimitModal!: boolean;
  @Prop({ default: null }) readonly thresholdLimitRequestedChannel!: 'liveChat' | 'call' | null;
  @Prop() readonly activeConversationTicket!: Conversation;
  @Prop() readonly onShowRequestLimitModal!: () => void;

  @userModule.State('isLogin') isLogin!: boolean;

  @channelLoadingModule.State('isLiveChatLoading') readonly isLiveChatLoading!: boolean;
  @channelLoadingModule.State('isVoiceChatLoading') readonly isVoiceChatLoading!: boolean;

  readonly MAX_DESCRIPTION_CHAR_NUM = MAX_DESCRIPTION_CHAR_NUM;
  readonly DISABLED_OPTION_WAIT_TIME = 60;

  get [PAGE_TRACKING]() {
    return {
      name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS}`,
      ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
    };
  }

  get openRequestField() {
    return {
      id: -10000,
      displayName: this.$t('global.description') as string,
      placeholder: this.connectConfig?.descriptionMessage || (this.$t('global.describe') as string),
      name: 'description',
      required: true,
    };
  }

  get descriptionLength() {
    return this.requestInfo.description?.length || 0;
  }

  get isDescriptionExceed() {
    return this.descriptionLength > MAX_DESCRIPTION_CHAR_NUM;
  }

  savedRequestInfo: { subject: string; description: string; email: string } | null = null;

  openRequestError = {
    description: false,
  };

  loading = false;
  isOpeningRequest = false;
  queuePosition = 0;

  errInfo = {
    isErr: false,
    message: '',
  };

  voiceChatAgentsOnline = false;
  waitTimes = {
    chat: null as null | string,
    call: null as null | string,
    order: {
      chat: 1,
      call: 2,
    },
  };

  onlineAgents = {
    chat: null as null | boolean,
    call: null as null | boolean,
  };

  longTimeWaiting = {
    MIN_WAIT_TIME: 10,
    selectedOption: null as 'liveChat' | 'call' | null,
  };

  refreshChannelsLoading = false;
  refreshChannelsThrottle = false;

  forceHide = {
    chat: false,
    call: false,
  };

  /** Used when enforcement is on: null = check in flight; true/false = pass/fail. Ignored for UX when tracking-only. */
  voiceReadinessOk: boolean | null = null;

  isInMaintenanceWindow = false;

  /** True when failed checks should change the UI (see voiceReadiness.ts UI toggle). */
  get voiceReadinessEnforcementActive() {
    return isVoiceReadinessEnforcementEnabled();
  }

  get voiceReadinessFailure() {
    return this.voiceReadinessEnforcementActive && this.voiceReadinessOk === false && this.hasCallOnlineAgents === true;
  }

  /** Live chat is shown and agents are online — safe to suggest as an alternative to voice. */
  get canSuggestLiveChatForVoiceReadiness() {
    return this.showLiveChat && this.hasLiveChatOnlineAgents === true;
  }

  get voiceReadinessUnavailableMessage(): string {
    if (this.canSuggestLiveChatForVoiceReadiness) {
      return this.$t('page.voice.readiness.unavailable_live_chat') as string;
    }
    if (this.voiceReadinessFailure && !this.showAddComment) {
      return this.$t('page.voice.readiness.unavailable_open_request') as string;
    }
    return this.$t('page.voice.readiness.unavailable_generic') as string;
  }

  get isVoiceCallDisabled() {
    if (!this.hasCallOnlineAgents || this.isLiveChatLoading) {
      return true;
    }
    if (!this.voiceReadinessEnforcementActive) {
      return false;
    }
    return this.voiceReadinessOk !== true;
  }

  get buttons() {
    if (!this.isOpeningRequest) {
      const shouldRefreshChat = this.showLoggedInOptions.showLiveChat && !this.hasLiveChatOnlineAgents;
      const shouldRefreshCalls = this.showLoggedInOptions.showCall && !this.hasCallOnlineAgents;
      if (shouldRefreshChat || shouldRefreshCalls) {
        return [
          {
            name: this.t('page.connect.refresh_channels.text'),
            id: 'refresh',
            isActive: false,
            appendIcon: this.refreshChannelsLoading ? LoadingIcon : null,
          },
        ];
      }

      return [];
    }

    return [
      {
        name: this.t('page.connect.open_request.text'),
        id: 'open-request',
        isActive: true,
      },
    ];
  }

  get isLiveChatLongTimeWaiting() {
    if (!this.showLiveChat || !this.hasLiveChatOnlineAgents) return false;

    const timeWaiting = Number(this.waitTimes.chat?.slice(1));
    return timeWaiting >= this.longTimeWaiting.MIN_WAIT_TIME;
  }

  get isCallLongTimeWaiting() {
    if (!this.showCall || !this.hasCallOnlineAgents) return false;

    const timeWaiting = Number(this.waitTimes.call?.slice(1));
    return timeWaiting >= this.longTimeWaiting.MIN_WAIT_TIME;
  }

  get hasAlternativeOption() {
    return (
      (this.showLiveChat && !this.isLiveChatLongTimeWaiting) ||
      (this.showCall && !this.isCallLongTimeWaiting) ||
      this.showAddComment ||
      this.showOpenRequest
    );
  }

  get isSelectedLongTimeWaitingOption() {
    return this.longTimeWaiting.selectedOption !== null;
  }

  get selectedLongTimeWaitingOption() {
    const isChatAndCallHighVolume = this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    if (isChatAndCallHighVolume) {
      return this.$t('page.connect.both_live_and_call');
    }

    if (this.isLiveChatLongTimeWaiting) {
      return this.$t('page.connect.live_chat');
    }

    if (this.isCallLongTimeWaiting) {
      return this.$t('page.connect.call');
    }
  }

  get isFromActiveConversation() {
    return Boolean(this.activeConversationTicket?.id);
  }

  get trackLogContext() {
    return this.isFromActiveConversation ? CMP_CONTEXTS.ACTIVE_CONVERSATION_TICKET : CMP_CONTEXTS.CONNECT_REQUEST;
  }

  openThresholdModal() {
    let dialog: {
      close(): void;
      loading(val: boolean): void;
    };

    const closeDialog = () => {
      dialog.close();
    };

    const onLiveChat = () => {
      this.select(1);
      closeDialog();
    };

    const onCall = () => {
      this.select(2);
      closeDialog();
    };

    const onOpenRequest = () => {
      this.select(3);
      closeDialog();
    };

    dialog = this.$dialog({
      componentBackground: 'transparent',
      content: this.$createElement(ThresholdLimitModal, {
        props: {
          liveChatWaitTimeDescription: this.liveChatWaitTimeDescription,
          callWaitTimeDescription: this.callWaitTimeDescription,
          hasLiveChatOnlineAgents: this.hasLiveChatOnlineAgents,
          hasCallOnlineAgents: this.hasCallOnlineAgents,
          requestedChannel: this.thresholdLimitRequestedChannel,
        },
        on: {
          liveChat: onLiveChat,
          call: onCall,
          openRequest: onOpenRequest,
          back: closeDialog,
        },
      }),
    });
  }

  getChatCallText(form: 'singular' | 'plural') {
    const choice = form === 'singular' ? 1 : 2;
    const isChatAndCallHighVolume = this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    if (isChatAndCallHighVolume) {
      return this.$tc('page.connect.long_wait_time.chats_calls', choice);
    }

    if (this.showLiveChat && this.isLiveChatLongTimeWaiting) {
      return this.$tc('page.connect.long_wait_time.chat', choice);
    }

    if (this.showCall && this.isCallLongTimeWaiting) {
      return this.$tc('page.connect.long_wait_time.call', choice);
    }
  }

  get stillPreferText() {
    return this.getChatCallText('singular');
  }

  get highVolumeText() {
    return this.getChatCallText('plural');
  }

  get suggestionText() {
    let text = '';

    const suggestLiveChat = this.showLiveChat && !this.isLiveChatLongTimeWaiting;
    const suggestCall = this.showCall && !this.isCallLongTimeWaiting;

    if (suggestLiveChat) {
      text += `${this.$t('page.connect.long_wait_time.start_a')} <strong>${this.$t(
        'page.connect.live_chat'
      )}</strong> `;
    } else if (suggestCall) {
      text += `<strong>${this.$t('page.connect.long_wait_time.call_us')}</strong> `;
    }

    if (this.showOpenRequest) {
      const showOr = suggestLiveChat || suggestCall;
      text += `${showOr ? this.$t('page.connect.long_wait_time.or') : ''} <strong>${this.$t(
        'page.connect.long_wait_time.open_a_request'
      )}</strong>`;
    }

    if (this.showAddComment) {
      const showOr = suggestLiveChat || suggestCall || this.showOpenRequest;
      text += `${showOr ? this.$t('page.connect.long_wait_time.or') : ''} <strong>${this.$t(
        'page.connect.follow_up.text'
      )}</strong>`;
    }

    return text;
  }

  get showLiveChat() {
    return !this.forceHide.chat && this.showLoggedInOptions.showLiveChat;
  }

  get hasLiveChatOnlineAgents() {
    return this.onlineAgents.chat;
  }

  get liveChatWaitTimeDescription() {
    return `${this.$tc('page.connect.customers_waiting', this.queuePosition, {
      customersNum: this.queuePosition,
    })} ${this.$t('page.connect.wait_time', {
      mins: this.isLiveChatLongTimeWaiting ? `> ${this.longTimeWaiting.MIN_WAIT_TIME}` : this.waitTimes.chat,
    })}`;
  }

  get showCall() {
    return !this.forceHide.call && this.showLoggedInOptions.showCall;
  }

  get hasCallOnlineAgents() {
    return this.onlineAgents.call;
  }

  get callWaitTimeDescription() {
    return `${this.$t('page.connect.wait_time', {
      mins: this.isCallLongTimeWaiting ? `> ${this.longTimeWaiting.MIN_WAIT_TIME}` : this.waitTimes.call,
    })}`;
  }

  get showOpenRequest() {
    const showOpenRequest = this.showLoggedInOptions.showOpenRequest;

    if (showOpenRequest) return true;

    if (this.voiceReadinessFailure && !this.canSuggestLiveChatForVoiceReadiness && !this.showAddComment) {
      return true;
    }

    return Boolean(
      !this.showAddComment &&
      (!this.showCall || !this.hasCallOnlineAgents) &&
      (!this.showLiveChat || !this.hasLiveChatOnlineAgents)
    );
  }

  get overloadedChannel() {
    const isChatAndCallHighVolume = this.isLiveChatLongTimeWaiting && this.isCallLongTimeWaiting;

    return isChatAndCallHighVolume
      ? OverloadedChannel.CHAT_AND_VOICE_CHAT
      : this.isLiveChatLongTimeWaiting
        ? OverloadedChannel.CHAT
        : this.isCallLongTimeWaiting
          ? OverloadedChannel.VOICE_CHAT
          : null;
  }

  async checkOnlineAgents() {
    const ticketId = this.activeConversationTicket?.id;
    const [onlineAgents, connectSettings] = await Promise.all([
      PleasedApi.getOnlineAgentsCheck({
        chat: this.showLiveChat,
        voice: this.showCall,
      }),
      ticketId
        ? PleasedApi.getActiveConversationConnectSettings({ ticketId }).catch(() => ({
            onlineChatAgentsCheck: true,
            onlineCallAgentsCheck: true,
          }))
        : Promise.resolve({
            onlineChatAgentsCheck: true,
            onlineCallAgentsCheck: true,
          } as PleasedModel.OnlineAgentsCheck),
    ]);

    if (ticketId) {
      if (!connectSettings.onlineChatAgentsCheck) {
        this.forceHide.chat = true;
        onlineAgents.onlineChatAgentsCheck = false;
      }

      if (!connectSettings.onlineCallAgentsCheck) {
        this.forceHide.call = true;
        onlineAgents.onlineCallAgentsCheck = false;
      }
    }

    this.setOnlineAgents(onlineAgents);

    analytics.track({
      name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_SHOWN}`,
      ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      metadata: {
        chatShown: this.showLiveChat,
        callShown: this.showCall,
        ...(!this.isFromActiveConversation ? { openRequestShown: this.showOpenRequest } : {}),
      },
    });
  }

  async refreshChannels() {
    // In case of double/several clicks throttle requests via waiting for 2 seconds
    if (this.refreshChannelsLoading) {
      this.refreshChannelsThrottle = true;
      await waitFor(2000);
      this.refreshChannelsThrottle = false;
      this.refreshChannelsLoading = false;
      return;
    }

    this.refreshChannelsLoading = true;
    await this.checkOnlineAgents();
    await this.runVoiceReadinessIfNeeded();

    // If there is no throttle, set loading to false
    if (!this.refreshChannelsThrottle) {
      this.refreshChannelsLoading = false;
    }
  }

  async runVoiceReadinessIfNeeded() {
    if (!this.showCall || !isVoiceReadinessTrackingEnabled()) {
      this.voiceReadinessOk = true;
      return;
    }
    if (this.voiceReadinessEnforcementActive) {
      this.voiceReadinessOk = null;
    }
    try {
      const { ok } = await runVoiceReadinessCheck();
      this.voiceReadinessOk = this.voiceReadinessEnforcementActive ? ok : true;
    } catch {
      this.voiceReadinessOk = this.voiceReadinessEnforcementActive ? false : true;
    }
  }

  async created() {
    document.title = 'Contact Options';
    this.savedRequestInfo = { ...this.requestInfo };

    this.loading = true;
    looselyCheckIfMaintenanceIsOngoing().then((isInMaintenance) => {
      this.isInMaintenanceWindow = isInMaintenance;
    });
    try {
      const [queue, _, waitTimes] = await Promise.all([
        PleasedApi.getQueuePosition().catch(() => ({ position: 1 })),
        this.checkOnlineAgents().then(() => this.runVoiceReadinessIfNeeded()),
        PleasedApi.getWaitTimes().catch(noop),
      ]);

      this.queuePosition = queue.position;
      this.setWaitTimes(waitTimes);
    } catch {}
    this.loading = false;
  }

  setOnlineAgents(onlineAgents: PleasedModel.OnlineAgentsCheck) {
    this.onlineAgents = {
      chat: onlineAgents.onlineChatAgentsCheck ?? null,
      call: onlineAgents.onlineCallAgentsCheck ?? null,
    };
  }

  setWaitTimes(waitTimes: PleasedModel.WaitingTime | null | void) {
    if (!waitTimes) return;
    const times = {
      chat: waitTimes?.averageChatWaitingTime || null,
      call: waitTimes?.averageCallWaitingTime || null,
    };

    const waitTimeOrders = Object.entries(times)
      .sort(([, time1], [, time2]) => {
        if (time1 === time2) return 0;
        if (time1 == null) return 1;
        if (time2 == null) return -1;
        const [numericTime1, numericTime2] = [time1.replace(/\D/g, '') || 0, time2.replace(/\D/g, '') || 0].map((n) =>
          Number(n)
        );
        return numericTime1 > numericTime2 ? 1 : -1;
      })
      .map(([type, value]) => [type, Number(value?.slice(1))]);

    const order = Object.fromEntries(waitTimeOrders);

    this.waitTimes = {
      ...times,
      order,
    };
  }

  handleWrapperSelect({ id }: { id: string }) {
    if (id === 'refresh') {
      analytics.trackClick({
        name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_REFRESH_CHANNEL}`,
        ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      });
      this.refreshChannels();
    } else if (id === 'open-request') {
      this.handleOpenRequest();
    }
  }

  handleOpenRequest() {
    reportingService.openRequestClicked();
    this.$gtag.event(GA_EVENT_NAME_ENUM.START_OPEN_REQUEST_CREATION);
    this.sendSelectedOptionLog(SelectedChannel.OPEN_REQUEST);
    this.loading = true;

    const hadAiConversation = this.connectConfig?.aiConversation;
    const { resumeAiSessionId, isFirstRequest, overlongConversation, selectedTransactionsData } = this
      .connectConfig || { resumeAiSessionId: null };

    // Generate tags from selectedTransactionsData if they exist
    const selectedTransactionTags =
      selectedTransactionsData && hasSelectedTransactions(selectedTransactionsData)
        ? generateSelectedTransactionsTags(selectedTransactionsData)
        : [];

    const tags = [
      this.connectConfig?.appliedMetadataAutomation?.tag,
      isFirstRequest ? 'ai::first_api_error' : isFirstRequest === false ? 'ai::api_error' : null,
      overlongConversation ? 'ai::exceed_msg_limit' : null,
      // TODO: Uncomment this when we have a way to handle selected transactions in open request
      //...selectedTransactionTags,
    ].filter(notEmpty);

    const payload = {
      subject: this.requestInfo.subject ?? 'Request',
      description: this.requestInfo.description,
      recipientAddress: this.requestInfo.email,
      ticketFields: this.ticketFields,
      userSelections: this.userSelections,
      userLogs: reportingService.getLogs(),
      tags,
      chatbotSessionId: analytics.sessionId,
      ...(hadAiConversation ? { aiSessionId: resumeAiSessionId || analytics.sessionId } : {}),
    };

    PleasedApi.createTicket(payload)
      .then((res) => {
        const $event: ConnectRequestEvent = {
          type: 'openRequest',
          payload: {
            ticketId: res.id,
          },
        };

        analytics.track({
          name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_CREATED}`,
          selection: res.id,
        });

        this.$gtag.event(GA_EVENT_NAME_ENUM.OPEN_REQUEST_CREATED);
        this.$emit('connect:request', $event);
      })
      .catch((e) => {
        const error: Error = e;

        if (isRequestLimitError(error)) {
          this.onShowRequestLimitModal();
          return;
        }

        this.$gtag.event(GA_EVENT_NAME_ENUM.OPEN_REQUEST_CREATION_FAILED);
        analytics.trackCriticalEvent({
          name: CRITICAL_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST_INITIATED_ERROR,
          metadata: {
            error: parseErrorMessage(error),
            payload,
          },
        });

        this.errInfo.isErr = true;
        this.errInfo.message = error.message;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async sendSelectedOptionLog(selectedChannel: SelectedChannelType) {
    // Track only when there is an overloaded channel
    if (!this.overloadedChannel) return;
    await PleasedApi.sendDeflectionLog({ selectedChannel, overloadedChannel: this.overloadedChannel });
  }

  select(id: number) {
    if (id === 1) {
      if (!this.hasLiveChatOnlineAgents || this.isVoiceChatLoading || this.isLiveChatLoading) return;

      if (this.isLiveChatLongTimeWaiting && !this.isSelectedLongTimeWaitingOption) {
        this.longTimeWaiting.selectedOption = 'liveChat';
        return;
      }

      const $event: ConnectRequestEvent = {
        type: 'chat',
        payload: {
          description: this.requestInfo.description,
          isFromActiveConversation: this.isFromActiveConversation,
        },
      };
      this.sendSelectedOptionLog(SelectedChannel.CHAT);
      this.$emit('connect:request', $event);
    } else if (id === 2) {
      if (!this.hasCallOnlineAgents || this.isVoiceChatLoading || this.isLiveChatLoading) return;
      if (this.voiceReadinessEnforcementActive && this.voiceReadinessOk !== true) return;

      if (this.isCallLongTimeWaiting && !this.isSelectedLongTimeWaitingOption) {
        this.longTimeWaiting.selectedOption = 'call';
        return;
      }

      const $event: ConnectRequestEvent = {
        type: 'voice',
        payload: {
          isFromActiveConversation: this.isFromActiveConversation,
          description: this.requestInfo.description,
          query: this.requestInfo.subject || '',
          deflectionLog: this.overloadedChannel
            ? { selectedChannel: SelectedChannel.VOICE_CHAT, overloadedChannel: this.overloadedChannel }
            : undefined,
        },
      };

      this.$emit('connect:request', $event);
    } else if (id === 3) {
      analytics.trackClick({
        name: `${this.trackLogContext}${COMPONENT_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST}`,
        ...(this.isFromActiveConversation ? { selection: this.activeConversationTicket.id } : {}),
      });

      // For guest users, start OTP verification process
      if (!this.isLogin) {
        // Track guest open request click
        analytics.trackClick({
          name: BTN_EVTS.GUEST_OPEN_REQUEST_CLICKED,
        });

        this.$emit('guest-open-request', {
          description: this.requestInfo.description,
        });
        return;
      }

      this.isOpeningRequest = true;
    } else if (id === 4) {
      const $event: ConnectRequestEvent = {
        type: 'addComment',
        payload: {},
      };
      this.sendSelectedOptionLog(SelectedChannel.OPEN_REQUEST);
      this.$emit('connect:request', $event);
    }
  }
  handleBack() {
    if (this.isOpeningRequest) {
      this.isOpeningRequest = false;
      this.$emit('update:request-info', this.savedRequestInfo);
      return;
    }

    if (this.isSelectedLongTimeWaitingOption) {
      this.longTimeWaiting.selectedOption = null;
      return;
    }

    this.$emit('back');
  }
  cleanErr() {
    this.errInfo.isErr = false;
    this.errInfo.message = '';
  }

  @Watch('showThresholdLimitModal')
  async onShowThresholdLimitModalChange() {
    if (this.showThresholdLimitModal) {
      await this.checkOnlineAgents();
      this.longTimeWaiting.selectedOption = null;
      this.openThresholdModal();
      this.$emit('set-show-threshold-limit-modal', false);
    }
  }

  @Watch('activeConversationTicket')
  async resetForceHide() {
    if (!this.isFromActiveConversation) {
      this.forceHide = {
        chat: false,
        call: false,
      };
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variable.scss';
.long-time-waiting {
  color: $sporty_red;
}
.queue-msg {
  font-size: 1.6rem;
  padding-top: 1.5rem;
  text-align: center;
  color: $contact-options-text-color;
}
#lo_live_chat {
  padding-left: 2.4rem;
  padding-right: 2.4rem;
}
#lo_call {
  padding-left: 3rem;
  padding-right: 3rem;
}
#lo_request {
  padding-left: 1.8rem;
  padding-right: 1.8rem;
}
.button-description-container {
  display: flex;
  align-items: center;
  gap: 4px;
}
.long-time-waiting-container {
  width: 90%;
  text-align: center;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;

  color: $contact-option-long-wait-time-text-color;
  font-size: 12px;

  .long-time-waiting-title {
    font-size: 14px;
    font-weight: 500;

    .highlight {
      color: $contact-option-long-wait-time-title-color;
    }
  }
}
.long-time-wait-still-prefer {
  width: 90%;
  text-align: center;
  color: $contact-option-long-wait-time-text-color;
  font-size: 12px;
}
.block-btn-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 12px;
}
.block-btn {
  width: 60%;
  text-align: center;
}
</style>
